import { appStore } from '../stores/appStore';
// Get store values
let client = null;
let token = '';
appStore.subscribe((state) => {
    client = state.client;
    token = state.token;
});
/**
 * Get all available billing plans
 */
export async function getBillingPlans() {
    if (!client)
        throw new Error('Client not initialized');
    const result = await client.getBillingPlans({ token });
    if (!result.response.successFull) {
        throw new Error(result.response.reason || 'Failed to fetch billing plans');
    }
    return {
        success: result.response.successFull,
        plans: result.response.plans.map((p) => ({
            id: p.id,
            name: p.name,
            displayName: p.displayName,
            priceMonthly: p.priceMonthly,
            priceYearly: p.priceYearly,
            currency: p.currency,
            storageLimitMb: p.storageLimitMb,
            bandwidthLimitGb: p.bandwidthLimitGb,
            features: p.features,
            isActive: p.isActive,
        })),
    };
}
/**
 * Get user's billing data (subscription + payment history)
 */
export async function getBillingData() {
    if (!client || !token)
        throw new Error('Not authenticated');
    // Get user subscription
    const subResult = await client.getUserSubscription({ token });
    if (!subResult.response.successFull) {
        throw new Error(subResult.response.reason || 'Failed to fetch subscription');
    }
    const currentPlan = subResult.response.currentPlan;
    const subscription = subResult.response.subscription;
    // Get payment history
    const historyResult = await client.getPaymentHistory({
        token,
        page: 1,
        pageSize: 50,
    });
    const paymentHistory = historyResult.response.transactions?.map((tx) => ({
        id: tx.id,
        amount: tx.amount,
        currency: tx.currency,
        date: new Date(tx.createdAt?.toDate() || '').toLocaleDateString(),
        status: tx.status,
        paymentMethod: tx.paymentMethod,
        invoiceNumber: tx.providerReference || tx.id,
        description: currentPlan ? `${currentPlan.displayName} subscription` : 'Subscription payment',
    })) || [];
    return {
        currentPlan: currentPlan
            ? {
                name: currentPlan.displayName,
                price: currentPlan.priceMonthly,
                currency: currentPlan.currency,
                interval: (subscription?.billingInterval || 'month'),
                features: currentPlan.features,
            }
            : null,
        nextPaymentDate: subscription
            ? new Date(subscription.currentPeriodEnd?.toDate() || '').toLocaleDateString()
            : '',
        paymentHistory,
    };
}
/**
 * Create a new subscription
 */
export async function createSubscription(planId, interval) {
    if (!client || !token)
        throw new Error('Not authenticated');
    const result = await client.createSubscription({
        token,
        planId,
        billingInterval: interval,
    });
    if (!result.response.successFull) {
        throw new Error(result.response.reason || 'Failed to create subscription');
    }
    return {
        subscription: {
            id: result.response.subscription?.id || '',
            status: result.response.subscription?.status || 'pending',
        },
        checkoutSessionId: result.response.checkoutSessionId,
    };
}
/**
 * Initiate payment for a subscription
 */
export async function initiatePayment(subscriptionId, paymentMethod, paymentDetails = {}, returnUrl) {
    if (!client || !token)
        throw new Error('Not authenticated');
    const result = await client.initiatePayment({
        token,
        subscriptionId,
        paymentMethod,
        returnUrl: returnUrl || window.location.origin + '/billing/success',
        paymentDetails,
    });
    if (!result.response.successFull) {
        throw new Error(result.response.reason || 'Failed to initiate payment');
    }
    return {
        transactionId: result.response.transactionId,
        checkoutUrl: result.response.checkoutUrl,
        paymentInstructions: result.response.paymentInstructions,
        metadata: result.response.metadata,
    };
}
/**
 * Get payment transaction status
 */
export async function getPaymentStatus(transactionId) {
    if (!client || !token)
        throw new Error('Not authenticated');
    const result = await client.getPaymentStatus({
        token,
        transactionId,
    });
    if (!result.response.successFull) {
        throw new Error(result.response.reason || 'Failed to get payment status');
    }
    return {
        status: result.response.transaction?.status || 'unknown',
        amount: result.response.transaction?.amount || 0,
        currency: result.response.transaction?.currency || 'USD',
    };
}
/**
 * Download invoice PDF
 */
export async function downloadInvoice(invoiceId) {
    if (!client || !token)
        throw new Error('Not authenticated');
    const result = await client.downloadInvoice({
        token,
        invoiceId,
    });
    if (!result.response.successFull) {
        throw new Error(result.response.reason || 'Failed to download invoice');
    }
    // Open download URL in new tab
    if (result.response.downloadUrl) {
        window.open(result.response.downloadUrl, '_blank');
    }
}
/**
 * Cancel subscription
 */
export async function cancelSubscription(cancelImmediately = false) {
    if (!client || !token)
        throw new Error('Not authenticated');
    const result = await client.cancelSubscription({
        token,
        cancelImmediately,
    });
    if (!result.response.successFull) {
        throw new Error(result.response.reason || 'Failed to cancel subscription');
    }
    return {
        subscription: result.response.subscription,
    };
}
